import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaBook,
    FaFileContract,
    FaUsers,
    FaCreditCard,
    FaExclamationTriangle,
    FaCheckCircle,
    FaClock,
    FaArrowRight,
} from 'react-icons/fa';
import Loading from '../../component/Loading';
import { formatDate } from '../../helper/helper';
import { canAccess } from '../../helper/permissions';

const formatIDR = (amount) =>
    new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
    }).format(Number(amount) || 0);

const monthLabel = (month) =>
    new Date(2000, (Number(month) || 1) - 1).toLocaleDateString('id-ID', { month: 'short' });

const DashboardAdmin = ({ data }) => {
    const navigate = useNavigate();
    const permissions = data?.user?.permissions || {};
    const shortcuts = [
        canAccess(permissions, 'loa', 'create') && { label: 'Tambah LOA', to: '/admin/loa/create' },
        canAccess(permissions, 'loa', 'view') && { label: 'Data LOA', to: '/admin/loa' },
        canAccess(permissions, 'journal', 'view') && { label: 'Journal', to: '/admin/journal' },
        canAccess(permissions, 'payment', 'view') && { label: 'Transaksi', to: '/admin/payment' },
    ].filter(Boolean);
    const [dashboardData, setDashboardData] = useState({
        totalJournals: 0,
        totalLoa: 0,
        totalUsers: 0,
        totalPayments: 0,
        paymentStatus: { pending: 0, lunas: 0 },
        recentLoa: [],
        recentPayments: [],
        monthlyStats: {
            year: new Date().getFullYear(),
            monthlyLoa: [],
            monthlyPayments: [],
        },
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const { data: overview } = await axios.get(`/api/dashboard/overview`, header);

            setDashboardData({
                totalJournals: overview.totalJournal || 0,
                totalLoa: overview.totalLoa || 0,
                totalUsers: overview.totalUsers || 0,
                totalPayments: overview.totalPayments || 0,
                paymentStatus: {
                    pending: overview.paymentStatus?.pending || 0,
                    lunas: overview.paymentStatus?.lunas || 0,
                },
                recentLoa: Array.isArray(overview.recentLoa) ? overview.recentLoa : [],
                recentPayments: Array.isArray(overview.recentPayments)
                    ? overview.recentPayments
                    : [],
                monthlyStats: {
                    year: overview.monthlyStats?.year || new Date().getFullYear(),
                    monthlyLoa: Array.isArray(overview.monthlyStats?.monthlyLoa)
                        ? overview.monthlyStats.monthlyLoa
                        : [],
                    monthlyPayments: Array.isArray(overview.monthlyStats?.monthlyPayments)
                        ? overview.monthlyStats.monthlyPayments
                        : [],
                },
            });
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Gagal memuat data dashboard');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line
    }, []);

    const paymentTotal =
        dashboardData.paymentStatus.pending + dashboardData.paymentStatus.lunas;
    const lunasPct =
        paymentTotal > 0
            ? Math.round((dashboardData.paymentStatus.lunas / paymentTotal) * 100)
            : 0;

    const maxMonthlyLoa = useMemo(() => {
        const counts = dashboardData.monthlyStats.monthlyLoa.map((i) => Number(i.count) || 0);
        return Math.max(...counts, 1);
    }, [dashboardData.monthlyStats.monthlyLoa]);

    const maxMonthlyPay = useMemo(() => {
        const counts = dashboardData.monthlyStats.monthlyPayments.map(
            (i) => Number(i.count) || 0,
        );
        return Math.max(...counts, 1);
    }, [dashboardData.monthlyStats.monthlyPayments]);

    const stats = [
        {
            title: 'Journal',
            value: dashboardData.totalJournals,
            icon: FaBook,
            to: '/admin/journal',
        },
        {
            title: 'LOA',
            value: dashboardData.totalLoa,
            icon: FaFileContract,
            to: '/admin/loa',
        },
        {
            title: 'Users',
            value: dashboardData.totalUsers,
            icon: FaUsers,
            to: '/admin/users',
        },
        {
            title: 'Payment',
            value: dashboardData.totalPayments,
            icon: FaCreditCard,
            to: '/admin/payment',
        },
    ];

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="card card-body py-12 text-center">
                <FaExclamationTriangle className="mx-auto mb-3 h-6 w-6 text-primary" />
                <h3 className="mb-1 text-xl font-medium text-ink dark:text-white">Error</h3>
                <p className="mb-6 text-base text-muted">{error}</p>
                <button
                    type="button"
                    onClick={fetchDashboardData}
                    className="btn-primary"
                >
                    Coba Lagi
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {stats.map((item) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.title}
                            type="button"
                            onClick={() => navigate(item.to)}
                            className="stat-card group"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-muted">
                                        {item.title}
                                    </p>
                                    <p className="mt-2 text-[28px] font-medium tabular-nums text-ink dark:text-white md:text-[36px]">
                                        {item.value.toLocaleString('id-ID')}
                                    </p>
                                </div>
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xs bg-surface-soft text-muted transition-colors duration-tesla group-hover:bg-hairline-soft dark:bg-white/5 dark:text-white/70">
                                    <Icon className="h-4 w-4" />
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="card lg:col-span-2">
                    <div className="card-body">
                        <div className="section-head">
                            <h3 className="section-title">Status Payment</h3>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/payment')}
                                className="text-link text-sm"
                            >
                                Lihat semua
                            </button>
                        </div>

                        <div className="mb-3 h-2 overflow-hidden rounded-full bg-surface-soft dark:bg-white/10">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-tesla"
                                style={{ width: `${lunasPct}%` }}
                            />
                        </div>
                        <p className="mb-4 text-xs text-muted">
                            {lunasPct}% lunas dari {paymentTotal.toLocaleString('id-ID')} transaksi
                            terhubung status
                        </p>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-xs bg-surface-soft px-4 py-4 dark:bg-white/5">
                                <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted">
                                    <FaClock className="h-3 w-3" />
                                    Pending
                                </div>
                                <p className="text-2xl font-medium text-ink dark:text-white">
                                    {dashboardData.paymentStatus.pending}
                                </p>
                            </div>
                            <div className="rounded-xs bg-surface-soft px-4 py-4 dark:bg-white/5">
                                <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted">
                                    <FaCheckCircle className="h-3 w-3" />
                                    Lunas
                                </div>
                                <p className="text-2xl font-medium text-ink dark:text-white">
                                    {dashboardData.paymentStatus.lunas}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="section-head">
                            <h3 className="section-title">Pintasan</h3>
                        </div>
                        <div className="space-y-2">
                            {shortcuts.map((link) => (
                                <button
                                    key={link.to}
                                    type="button"
                                    onClick={() => navigate(link.to)}
                                    className="flex w-full items-center justify-between rounded-xs bg-surface-soft px-4 py-3 text-sm text-ink transition-colors duration-tesla hover:bg-hairline-soft dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                                >
                                    <span>{link.label}</span>
                                    <FaArrowRight className="h-3 w-3 text-muted" />
                                </button>
                            ))}
                            {!shortcuts.length ? (
                                <p className="text-xs text-muted-soft">Tidak ada pintasan tersedia.</p>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="card overflow-hidden">
                    <div className="card-body !pb-0">
                        <div className="section-head">
                            <h3 className="section-title">LOA Terbaru</h3>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/loa')}
                                className="text-link text-sm"
                            >
                                Semua
                            </button>
                        </div>
                    </div>
                    <div>
                        {dashboardData.recentLoa.length > 0 ? (
                            dashboardData.recentLoa.slice(0, 5).map((loa, index) => (
                                <button
                                    key={loa.loa_id || index}
                                    type="button"
                                    onClick={() =>
                                        loa.loa_id && navigate(`/admin/loa/${loa.loa_id}`)
                                    }
                                    className="flex w-full items-start gap-3 px-6 py-3 text-left transition-colors duration-tesla hover:bg-surface-soft dark:hover:bg-white/5"
                                >
                                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xs bg-surface-soft text-muted dark:bg-white/5">
                                        <FaFileContract className="h-3.5 w-3.5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 text-sm font-medium text-ink dark:text-white">
                                            {loa.title || 'LOA tanpa judul'}
                                        </p>
                                        <p className="mt-0.5 line-clamp-1 text-xs text-muted">
                                            {loa.journal_id?.journal_name || 'Journal'} ·{' '}
                                            {loa.author || '-'} · {formatDate(loa.date)}
                                        </p>
                                    </div>
                                </button>
                            ))
                        ) : (
                            <p className="px-6 py-8 text-center text-sm text-muted-soft">
                                Belum ada LOA
                            </p>
                        )}
                    </div>
                </div>

                <div className="card overflow-hidden">
                    <div className="card-body !pb-0">
                        <div className="section-head">
                            <h3 className="section-title">Payment Terbaru</h3>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/payment')}
                                className="text-link text-sm"
                            >
                                Semua
                            </button>
                        </div>
                    </div>
                    <div>
                        {dashboardData.recentPayments.length > 0 ? (
                            dashboardData.recentPayments.slice(0, 5).map((payment, index) => {
                                const isLunas = payment.paymentStatus === 'lunas';
                                return (
                                    <div
                                        key={payment.paymentID || index}
                                        className="flex items-start gap-3 px-6 py-3"
                                    >
                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xs bg-surface-soft text-muted dark:bg-white/5">
                                            <FaCreditCard className="h-3.5 w-3.5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="line-clamp-1 text-sm font-medium text-ink dark:text-white">
                                                    {payment.paymentName || payment.trxID || 'Payment'}
                                                </p>
                                                <span className="tag shrink-0 capitalize">
                                                    {isLunas ? 'Lunas' : 'Pending'}
                                                </span>
                                            </div>
                                            <p className="mt-0.5 line-clamp-1 text-xs text-muted">
                                                {payment.customerName || '-'} ·{' '}
                                                {formatIDR(payment.amount)} ·{' '}
                                                {payment.createAt
                                                    ? formatDate(payment.createAt)
                                                    : '-'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="px-6 py-8 text-center text-sm text-muted-soft">
                                Belum ada payment
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {(dashboardData.monthlyStats.monthlyLoa.length > 0 ||
                dashboardData.monthlyStats.monthlyPayments.length > 0) && (
                <div className="card">
                    <div className="card-body">
                        <div className="section-head">
                            <h3 className="section-title">
                                Statistik Bulanan {dashboardData.monthlyStats.year}
                            </h3>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2">
                            {dashboardData.monthlyStats.monthlyLoa.length > 0 && (
                                <div>
                                    <p className="mb-3 text-xs font-medium text-muted">
                                        LOA per bulan
                                    </p>
                                    <div className="space-y-2">
                                        {dashboardData.monthlyStats.monthlyLoa.map((item, index) => {
                                            if (!item) return null;
                                            const count = Number(item.count) || 0;
                                            const pct = Math.round((count / maxMonthlyLoa) * 100);
                                            return (
                                                <div key={index} className="flex items-center gap-3">
                                                    <span className="w-10 shrink-0 text-xs font-medium text-muted">
                                                        {monthLabel(item.month)}
                                                    </span>
                                                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-soft dark:bg-white/10">
                                                        <div
                                                            className="h-full rounded-full bg-primary"
                                                            style={{ width: `${pct}%` }}
                                                        />
                                                    </div>
                                                    <span className="w-8 text-right text-xs font-medium text-ink dark:text-white/90">
                                                        {count}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {dashboardData.monthlyStats.monthlyPayments.length > 0 && (
                                <div>
                                    <p className="mb-3 text-xs font-medium text-muted">
                                        Payment per bulan
                                    </p>
                                    <div className="space-y-2">
                                        {dashboardData.monthlyStats.monthlyPayments.map(
                                            (item, index) => {
                                                if (!item) return null;
                                                const count = Number(item.count) || 0;
                                                const pct = Math.round((count / maxMonthlyPay) * 100);
                                                return (
                                                    <div key={index} className="flex items-center gap-3">
                                                        <span className="w-10 shrink-0 text-xs font-medium text-muted">
                                                            {monthLabel(item.month)}
                                                        </span>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="mb-0.5 flex items-center justify-between gap-2">
                                                                <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-soft dark:bg-white/10">
                                                                    <div
                                                                        className="h-full rounded-full bg-primary"
                                                                        style={{ width: `${pct}%` }}
                                                                    />
                                                                </div>
                                                                <span className="shrink-0 text-xs font-medium text-ink dark:text-white/90">
                                                                    {count}
                                                                </span>
                                                            </div>
                                                            <p className="text-[10px] text-muted-soft">
                                                                {formatIDR(item.totalAmount)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardAdmin;
